// APIs
import { getDispositions } from "@/DataPages/api/crm/queries";

// Contexts
import { useCRMContext } from "@/DataPages/context/crm/CRMContext";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Hooks
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";
import { usePaginatedResource } from "@/Common/hooks/Pagination";

// MUI
import { Grid, Skeleton, Stack, Typography } from "@mui/material";

// Layouts
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";

// Components
import DispositionsTable from "./LeadDispositionsTable";

// Helpers
import { and } from "@/Common/helpers/sonar";

// Images
import emptyTable from "@/assets/images/commons/empty_table.png";

// Constants
import { EMPTY_RESULT, INITIAL_PAGE_PARAM, PAGINATION_SIZE } from "@/DataPages/constants/pagination";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";
import { IPageParam } from "@/Common/api/types/pagination";

// Constants
import { CRM_KEYS } from "../constants";

type TLeadDispositionsProps = {
	isActiveTab: boolean;
};

function ContentTitle(props: Readonly<{ title: string; isLoading?: boolean }>) {
	const { title } = props;
	return <Typography className={"page-title"}>{title}</Typography>;
}

export default function LeadDispositions(props: Readonly<TLeadDispositionsProps>) {
	const { isActiveTab } = props;
	const { locationId = "", selection } = useCRMContext();
	const { cycleId: commissionCycleId = "", payPeriodId = "" } = selection ?? {};
	const { commissionCycle, payPeriod } = useCommissionCyclesData({ commissionCycleId, payPeriodId });

	const {
		data: rows,
		isLoading,
		refetch,
		hasNextPage,
		isFetching,
	} = usePaginatedResource({
		queryKey: [CRM_KEYS.QUERY.DISPOSITION, locationId, commissionCycleId, payPeriodId],
		queryFn: async (params) => {
			const pageParam = params.pageParam as IPageParam<string> | undefined;
			const pagination = pageParam && typeof pageParam === "object" ? pageParam : INITIAL_PAGE_PARAM;
			if (!("after" in pagination) || !("first" in pagination)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}
			const paginatedResponse = await getDispositions(locationId, commissionCycleId, payPeriodId, pagination);
			if (!paginatedResponse) {
				return EMPTY_RESULT;
			}
			return paginatedResponse;
		},
		initialPageParam: INITIAL_PAGE_PARAM,
		getNextPageParam: buildGetNextPageParam(PAGINATION_SIZE),
		enabled: and(isActiveTab, !!locationId, !!commissionCycleId, !!payPeriodId),
		fetchAllPages: true,
		staleTime: Infinity,
		gcTime: Infinity,
	});

	const { getSubscription } = useSubscriptions();
	const dispositionsSubscription = getSubscription([CRM_KEYS.SUBSCRIPTION.DISPOSITION, locationId, commissionCycleId, payPeriodId]);
	dispositionsSubscription?.setInvalidate([CRM_KEYS.SUBSCRIPTION.DISPOSITION, locationId, commissionCycleId, payPeriodId]);
	dispositionsSubscription?.subscriptions?.onUpdateDispositions?.setCallback(() => refetch());

	const dataToShow = Boolean(rows?.length);

	if (isLoading) {
		return (
			<Grid item xs={12} mt={1}>
				<Skeleton className="relative top-[0px]" variant="text" width={500} height={30} />
				<TableLoader toolbar buttons={3} content rows={5} multiTitles={5} />
			</Grid>
		);
	}

	return (
		<Grid container alignContent="flex-start" className="h-full">
			<Grid container item>
				<Grid item xs={12} height={512}>
					{!dataToShow ? (
						<EmptyBody image={emptyTable} title={`No Dispositions to show`} bodyHeight={"435px"} className="!shadow-none" />
					) : (
						<Stack className="h-full">
							<ContentTitle title={`${commissionCycle?.title} ${payPeriod?.title} Dispositions`} />
							<DispositionsTable rows={rows} hasNextPage={hasNextPage} isFetching={isFetching} />
						</Stack>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
}
